const STORAGE_KEY = 'rw-active-tenant';
const LAST_PROJECT_KEY = 'rw-last-project';

const listeners = new Set<() => void>();

let scopedUserId: string | undefined;

function scopedKey(base: string, userId = scopedUserId): string {
  return userId ? `${base}:${userId}` : base;
}

function readUserValue(base: string): string | undefined {
  if (typeof window === 'undefined') return undefined;
  if (scopedUserId) {
    const scoped = window.localStorage.getItem(scopedKey(base));
    if (scoped) return scoped;
    const legacy = window.localStorage.getItem(base);
    if (legacy) {
      window.localStorage.setItem(scopedKey(base), legacy);
      window.localStorage.removeItem(base);
      return legacy;
    }
    return undefined;
  }
  return window.localStorage.getItem(base) ?? undefined;
}

function writeUserValue(base: string, value: string): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(scopedKey(base), value);
}

function removeUserValue(base: string): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(scopedKey(base));
}

function notify(): void {
  for (const listener of listeners) listener();
}

export function setWorkspaceScope(userId: string | undefined): void {
  if (scopedUserId === userId) return;
  scopedUserId = userId;
  if (!userId && typeof window !== 'undefined') {
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(LAST_PROJECT_KEY);
  }
  notify();
}

/** Identity of the cached data: the signed-in user plus the organization sent as `x-tenant-id`. */
export function getWorkspaceScopeKey(): string {
  return `${scopedUserId ?? ''}:${getActiveTenantId() ?? ''}`;
}

export function getActiveTenantId(): string | undefined {
  return readUserValue(STORAGE_KEY);
}

export function setActiveTenantId(tenantId: string): void {
  if (getActiveTenantId() === tenantId) return;
  writeUserValue(STORAGE_KEY, tenantId);
  notify();
}

export function clearActiveTenantId(): void {
  if (!getActiveTenantId()) return;
  removeUserValue(STORAGE_KEY);
  notify();
}

export function subscribeActiveTenant(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getLastProjectId(): string | undefined {
  return readUserValue(LAST_PROJECT_KEY);
}

export function setLastProjectId(projectId: string): void {
  writeUserValue(LAST_PROJECT_KEY, projectId);
}

export function clearLastProjectId(): void {
  removeUserValue(LAST_PROJECT_KEY);
}
